/**
 * The quick choices above the management reports: periods for the reports
 * that cover a stretch of time, and "as at" days for the ones that show where
 * things stand on a date. All days are yyyy-mm-dd in the school's own calendar.
 */

export interface TermLike {
  term: string;
  academic_year_name?: string;
  start_date: string;
  end_date: string;
  is_current?: boolean;
}

export interface QuickPeriod {
  key: string;
  label: string;
  start: string;
  end: string;
}

export interface QuickDay {
  key: string;
  label: string;
  day: string;
}

const pad = (value: number) => String(value).padStart(2, "0");

/** The last day of a month; `month` is 1-12. */
const monthEnd = (year: number, month: number) => `${year}-${pad(month)}-${pad(new Date(Date.UTC(year, month, 0)).getUTCDate())}`;

/** "term_2" → "Term 2". */
export const termLabel = (term: TermLike) => term.term.replace(/^term_/, "Term ");

/** The term today falls in; failing that, the one marked current. */
export function currentTerm<T extends TermLike>(today: string, terms: T[]): T | undefined {
  return terms.find((term) => term.start_date <= today && today <= term.end_date) ?? terms.find((term) => term.is_current);
}

/** The latest term that has already ended. */
export function lastEndedTerm<T extends TermLike>(today: string, terms: T[]): T | undefined {
  return terms
    .filter((term) => term.end_date < today)
    .sort((a, b) => b.end_date.localeCompare(a.end_date))[0];
}

export function quickPeriods(today: string, terms: TermLike[] = []): QuickPeriod[] {
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));
  const lastMonthYear = month === 1 ? year - 1 : year;
  const lastMonth = month === 1 ? 12 : month - 1;
  const periods: QuickPeriod[] = [
    { key: "month", label: "This month", start: `${today.slice(0, 7)}-01`, end: today },
    { key: "last-month", label: "Last month", start: `${lastMonthYear}-${pad(lastMonth)}-01`, end: monthEnd(lastMonthYear, lastMonth) },
  ];
  const term = currentTerm(today, terms);
  if (term && term.start_date <= today) {
    periods.push({
      key: "term",
      label: `This term (${termLabel(term)})`,
      start: term.start_date,
      end: term.end_date < today ? term.end_date : today,
    });
  }
  periods.push(
    { key: "ytd", label: "Year to date", start: `${year}-01-01`, end: today },
    { key: "last-year", label: "Last year", start: `${year - 1}-01-01`, end: `${year - 1}-12-31` },
  );
  return periods;
}

export function quickDays(today: string, terms: TermLike[] = []): QuickDay[] {
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));
  const days: QuickDay[] = [
    { key: "today", label: "Today", day: today },
    { key: "month-end", label: "End of last month", day: month === 1 ? `${year - 1}-12-31` : monthEnd(year, month - 1) },
  ];
  const term = lastEndedTerm(today, terms);
  if (term) days.push({ key: "term-end", label: `End of ${termLabel(term)}${term.academic_year_name ? ` ${term.academic_year_name}` : ""}`, day: term.end_date });
  days.push({ key: "year-end", label: "End of last year", day: `${year - 1}-12-31` });
  return days;
}

/** The quick period these dates are, or "" when they were typed in. */
export const matchingPeriod = (periods: QuickPeriod[], start: string, end: string) =>
  periods.find((period) => period.start === start && period.end === end)?.key ?? "";

export const matchingDay = (days: QuickDay[], day: string) => days.find((entry) => entry.day === day)?.key ?? "";
